<template>
  <div v-if="dispatchMission.type === DispatchType.Mission">
    <div class="red-dot ml-3 flex items-center justify-between gap-2">
      <div class="text-sm">
        {{ $t(`mission.target`) }}
      </div>
      <div class="flex items-center gap-2" v-if="isEdit">
        <template v-if="!dispatchMission.objectiveSignature">
          <button
            class="btn btn-secondary btn-xs"
            @click="clickCustom"
            v-if="!isWorldMission"
          >
            设定目标
          </button>
          <!-- <button class="btn btn-primary btn-xs" @click="clickGenerate">
            <PowerIcon />
            1
            <span>{{
              $t(
                dispatchMission.objective
                  ? `Buttons.evaluate`
                  : `Buttons.generate`
              )
            }}</span>
          </button> -->
        </template>
        <template v-else-if="!showDetail">
          <button class="btn btn-outline btn-xs" @click="clickCustom">
            <i class="iconify mingcute--edit-2-line" />
            编辑
          </button>
        </template>
      </div>
    </div>

    <div class="px-3">
      <div
        :class="[
          'text-base-content/50 my-1',
          showDetail ? 'line-clamp-none' : 'line-clamp-2',
        ]"
      >
        {{ dispatchMission.objective || $t(`mission.noTarget`) }}
      </div>

      <VanCellGroup v-if="dispatchMission.difficultyLevel">
        <template v-if="showDetail">
          <VanCell :class="[!showDetail && 'py-1']">
            <template #title>
              <div>
                <span class="text-xs">任务难度：</span>
                <span
                  :class="`difficulty-level${dispatchMission.difficultyLevel}`"
                >
                  {{
                    $t(
                      `mission.difficultyLevel.${dispatchMission.difficultyLevel}`
                    )
                  }}
                </span>

                <div
                  v-if="dispatchMission.difficultyLevelReason"
                  class="text-xs"
                >
                  <span class="text-base-content/80"> 原因： </span>
                  <span class="text-base-content/50">
                    {{ dispatchMission.difficultyLevelReason }}
                  </span>
                </div>
              </div>
            </template>
            <template
              #label
              v-if="extraChanceDetail.rare > 0 || extraChanceDetail.epic > 0"
            >
              <div>
                <div>难度奖励:</div>
                <div class="text-success/70">
                  +{{ extraChanceDetail.rare }}%获取罕见道具概率
                </div>
                <div class="text-info/70">
                  +{{ extraChanceDetail.epic }}%获取稀有道具概率
                </div>
                <div v-if="returnPowerCube">
                  任务成功返还能量块: {{ returnPowerCube }}
                </div>
              </div>
            </template>
          </VanCell>

          <VanCell :class="[!showDetail && 'py-1']">
            <template #title>
              <div>
                <span class="text-xs">任务成功率：</span>
                <span :class="`${successRateText}`">
                  {{ $t(`mission.rateText.${successRateText}`) }}
                  <!-- ({{ successRate }}%) -->
                </span>
              </div>
            </template>
            <template
              #label
              v-if="
                rateDetail.userExtraRate > 0 ||
                rateDetail.equipmentExtraRate > 0 ||
                rateDetail.previousMissionExtraRate > 0 ||
                dispatchMission.successProbability! - successRate > 0 
              "
            >
              <div>
                <div
                  class="text-error/80"
                  v-if="
                    [
                      DispatchDifficultyLevel.Nightmare,
                      DispatchDifficultyLevel.Hell,
                    ].includes(dispatchMission.difficultyLevel)
                  "
                >
                  {{
                    $t(
                      `mission.difficultyLevel.${dispatchMission.difficultyLevel}`
                    )
                  }}难度下，增益效果衰减
                  {{ DispatchDifficultyLevel.Nightmare ? "50%" : "75%" }}
                </div>
                <div v-if="rateDetail.userExtraRate > 0">
                  多人任务奖励: +{{ fixNumber(rateDetail.userExtraRate, 100) }}%
                </div>
                <div v-if="rateDetail.equipmentExtraRate > 0">
                  道具奖励: +{{
                    fixNumber(rateDetail.equipmentExtraRate, 100)
                  }}%
                </div>
                <div v-if="rateDetail.previousMissionExtraRate > 0">
                  前情奖励: +{{
                    fixNumber(rateDetail.previousMissionExtraRate, 100)
                  }}%
                </div>
                <!-- {{ dispatchMission.successProbability }}
                {{ successRate }} -->
                <div
                  v-if="
                    dispatchMission.successProbability &&
                    dispatchMission.successProbability - successRate > 0
                  "
                >
                  重复任务: +{{
                    fixNumber(
                      dispatchMission.successProbability - successRate,
                      100,
                      2
                    )
                  }}%
                </div>
              </div>
            </template>
          </VanCell>
        </template>
        <template v-else>
          <VanCell class="p-1">
            <template #title>
              <div class="flex items-center gap-2 justify-between">
                <div class="flex items-center gap-2">
                  <div>
                    <span class="text-xs">难度：</span>
                    <span
                      :class="`difficulty-level${dispatchMission.difficultyLevel}`"
                    >
                      {{
                        $t(
                          `mission.difficultyLevel.${dispatchMission.difficultyLevel}`
                        )
                      }}
                    </span>
                  </div>
                  <div>
                    <span class="text-xs">成功率：</span>
                    <span :class="`${successRateText}`">
                      {{ $t(`mission.rateText.${successRateText}`) }}
                      <!-- ({{ successRate }}%) -->
                    </span>
                  </div>
                </div>

                <button class="btn btn-xs btn-outline" @click="clickShowDetail">
                  <i class="iconify mingcute--eye-line" />
                  详情
                </button>
              </div>
            </template>
          </VanCell>
        </template>
      </VanCellGroup>

      <VanCellGroup>
        <VanCell class="my-1 p-1">
          <template #title>
            <div class="flex items-center gap-2 justify-between">
              <div class="text-xs flex items-center gap-1">
                <span class="text-success">参与活跃榜</span>
                <i class="iconify ph--ranking text-success mr-1" />
                <button
                  class="btn btn-xs btn-outline"
                  @click="showRankingModal = true"
                >
                  查看榜单
                </button>
              </div>

              <VanSwitch
                v-if="!isWorldMission"
                :model-value="dispatchMission.isRankingEnabled"
                @update:model-value="toggleRanking"
                size="12"
                :disabled="showDetail"
              />
            </div>
          </template>

          <template
            #label
            v-if="
              dispatchMission.isRankingEnabled &&
              dispatchMission.difficultyLevel
            "
          >
            <MissionEditBottomSeasonScore
              :dispatch-mission="dispatchMission"
              :show-detail="showDetail"
            />
          </template>
        </VanCell>
      </VanCellGroup>
    </div>

    <VanPopup
      v-model:show="showDetailModal"
      v-if="!showDetail"
      position="bottom"
      class="px-4 py-5 max-h-[80%]"
      closeable
    >
      <MissionEditBottomSeason
        :dispatch-mission="dispatchMission"
        :show-detail="true"
      />
    </VanPopup>

    <MissionSeasonModal
      v-model:show="showSeasonModal"
      :dispatch-mission="dispatchMission"
      :leader-mate="leaderMate"
    />

    <VanPopup
      v-model:show="showRankingModal"
      position="bottom"
      class="pt-5 h-[80%]"
    >
      <SeasonRankHome :show-title="true" />
    </VanPopup>
  </div>
</template>

<script setup lang="ts">
import {
  DispatchDifficultyLevel,
  DispatchType,
  EquipmentRarity,
} from "@/enums/mission";
import { Mate, MateDispatch } from "@/types/entity";
import { computed, ref } from "vue";
import {
  fixNumber,
  getEquipmentExtraRate,
  getExtraChance,
  getPreviousMissionExtraRate,
  getReturnPowerCube,
  getSuccessRateText,
  getUserExtraRate,
  levelBaseRate,
} from "./MissionStat";
import MissionSeasonModal from "./MissionSeasonModal.vue";
import { showConfirmDialog, showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import SeasonRankHome from "../mobile/rank/SeasonRankHome.vue";
import { StorageGet, StorageSet } from "@/tools/localStorage";

const { t } = useI18n();

const showSeasonModal = ref(false);
const showRankingModal = ref(false);

const props = defineProps<{
  dispatchMission: MateDispatch & { objectiveSignature: string };
  showDetail?: boolean;
  leaderMate?: Mate;
  isEdit?: boolean;
  isWorldMission?: boolean;
}>();

const toggleRanking = async (value: boolean) => {
  const key = `know:rank`;
  const knowRank = StorageGet(key);

  if (value && !knowRank) {
    try {
      await showConfirmDialog({
        message:
          "开启后，您OC此后生成的冒险手帐将自动设为公开状态，并有机会登上活跃榜，向所有小伙伴分享TA的精彩瞬间！历史手帐的隐私状态不会改变。",
      });
      StorageSet(key, 1);
    } catch {
      return;
    }
  }

  const { dispatchMission } = props;
  dispatchMission.isRankingEnabled = value;
};

const rateDetail = computed(() => {
  let extraBaseRate = 1;

  const { difficultyLevel } = props.dispatchMission;
  if (difficultyLevel === DispatchDifficultyLevel.Nightmare) {
    extraBaseRate = 0.5;
  } else if (difficultyLevel === DispatchDifficultyLevel.Hell) {
    extraBaseRate = 0.25;
  }

  return {
    baseRate: levelBaseRate(props.dispatchMission.difficultyLevel!),
    equipmentExtraRate:
      getEquipmentExtraRate(props.dispatchMission.carriedEquipmentzz || []) *
      extraBaseRate,
    userExtraRate:
      getUserExtraRate(props.dispatchMission.dispatchMemberzz || []) *
      extraBaseRate,
    previousMissionExtraRate:
      getPreviousMissionExtraRate(props.dispatchMission as MateDispatch) *
      extraBaseRate,
  };
});

const returnPowerCube = computed(() => {
  return getReturnPowerCube(props.dispatchMission.difficultyLevel!);
});

const successRate = computed(() => {
  const {
    baseRate,
    equipmentExtraRate,
    userExtraRate,
    previousMissionExtraRate,
  } = rateDetail.value;

  return fixNumber(
    baseRate + equipmentExtraRate + userExtraRate + previousMissionExtraRate
  );
});

const successRateText = computed(() => {
  return getSuccessRateText(
    props.dispatchMission.successProbability ?? successRate.value
  );
});
const extraChanceDetail = computed(() => {
  const res = getExtraChance(props.dispatchMission.difficultyLevel!);
  return {
    rare: res[EquipmentRarity.Rare],
    epic: res[EquipmentRarity.Epic],
  };
});

const clickCustom = () => {
  if (!props.leaderMate) return showFailToast(t(`mission.mate.selectFirst`));

  showSeasonModal.value = true;
};

const showDetailModal = ref(false);

const clickShowDetail = () => {
  showDetailModal.value = true;
};

// const clickGenerate = () => {
//   if (!props.leaderMate) return showFailToast(t(`mission.mate.selectFirst`));
//   if (!props.dispatchMission.location)
//     return showFailToast(t(`mission.location.selectFirst`));
//   if (!props.dispatchMission.mission)
//     return showFailToast(t(`mission.mission.selectFirst`));
//   generateObjective(props.dispatchMission);
// };
</script>

<style scoped lang="postcss">
.highest,
.high {
  @apply text-success;
}

.medium {
  @apply text-info;
}

.low,
.lowest {
  @apply text-error;
}
</style>
